// src/components/SkillsManager.js
// Full-page skill management: create, edit, delete, upload, set default.
// Includes real-time validation with highlighted violations and do's/don'ts guidance.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import skillService from '../services/skillService';
import '../styles/SkillsManager.css';

// ════════════════════════════════════════════════════════════
// GUIDANCE CONTENT
// ════════════════════════════════════════════════════════════

const GUIDANCE = {
  dos: [
    { icon: '✅', text: 'Response formatting — "Show amounts in Lakhs (÷ 100,000)"' },
    { icon: '✅', text: 'Business rules — "When comparing periods, always use YoY not QoQ"' },
    { icon: '✅', text: 'Default groupings — "Always break down by Region first, then Brand"' },
    { icon: '✅', text: 'Filters — "Exclude Modern Trade channel unless I specifically ask"' },
    { icon: '✅', text: 'Analysis depth — "Keep responses under 3 bullet points"' },
    { icon: '✅', text: 'Domain vocabulary — "When I say primary, I mean Sales Invoices"' },
    { icon: '✅', text: 'Tone & style — "Be concise and use tables for comparisons"' },
    { icon: '✅', text: 'Action items — "Always suggest one actionable recommendation"' },
  ],
  donts: [
    { icon: '🚫', text: 'Table names — the AI already knows your data schema' },
    { icon: '🚫', text: 'Column names like INV_DT, DIST_CD, TXN_TYPE — managed by the system' },
    { icon: '🚫', text: 'SQL statements (CREATE, DROP, INSERT, DELETE, UPDATE)' },
    { icon: '🚫', text: 'Schema references like AI_SALES_BOT.SECONDARY_SALES' },
    { icon: '🚫', text: 'System table names (USERSMANAGER, QUESTION_HISTORY_RAW, etc.)' },
    { icon: '🚫', text: 'Prompt overrides like "ignore previous instructions" or "you are now..."' },
  ],
};

// ════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════

const SkillsManager = ({ onClose, onSkillsChanged }) => {
  // Skill list state
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Editor state
  const [editorMode, setEditorMode] = useState(null); // null | 'create' | 'edit'
  const [editingSkill, setEditingSkill] = useState(null);
  const [formName, setFormName] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formIsDefault, setFormIsDefault] = useState(false);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  // Validation state
  const [violations, setViolations] = useState([]);
  const validationTimerRef = useRef(null);

  // Upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  // Delete confirmation
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Guidance panel
  const [showGuidance, setShowGuidance] = useState(false);

  // ── Load skills ──
  const fetchSkills = useCallback(async () => {
    setLoading(true);
    try {
      const data = await skillService.listSkills();
      setSkills(data.skills || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSkills();
  }, [fetchSkills]);

  // ── Real-time validation (debounced) ──
  useEffect(() => {
    if (validationTimerRef.current) {
      clearTimeout(validationTimerRef.current);
    }

    if (!formContent.trim()) {
      setViolations([]);
      return;
    }

    validationTimerRef.current = setTimeout(() => {
      const result = skillService.validateContentLocally(formContent);
      setViolations(result.violations || []);
    }, 300); // 300ms debounce

    return () => {
      if (validationTimerRef.current) clearTimeout(validationTimerRef.current);
    };
  }, [formContent]);

  // ── Editor actions ──

  const openCreateEditor = () => {
    setEditorMode('create');
    setEditingSkill(null);
    setFormName('');
    setFormContent('');
    setFormDescription('');
    setFormIsDefault(false);
    setFormError(null);
    setViolations([]);
  };

  const openEditEditor = (skill) => {
    setEditorMode('edit');
    setEditingSkill(skill);
    setFormName(skill.name || '');
    setFormContent(skill.content || '');
    setFormDescription(skill.description || '');
    setFormIsDefault(skill.is_default || false);
    setFormError(null);
    setViolations([]);
  };

  const closeEditor = () => {
    setEditorMode(null);
    setEditingSkill(null);
    setFormError(null);
    setViolations([]);
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      setFormError('Skill name is required');
      return;
    }
    if (!formContent.trim()) {
      setFormError('Skill content is required');
      return;
    }

    // Check client-side validation
    const validation = skillService.validateContentLocally(formContent);
    if (!validation.valid) {
      setFormError(`Content has ${validation.violations.length} blocked pattern(s). Fix highlighted issues below.`);
      return;
    }

    setFormSaving(true);
    setFormError(null);

    try {
      let result;
      if (editorMode === 'create') {
        result = await skillService.createSkill({
          name: formName.trim(),
          content: formContent.trim(),
          description: formDescription.trim(),
          is_default: formIsDefault,
        });
      } else {
        result = await skillService.updateSkill(editingSkill.skill_id, {
          name: formName.trim(),
          content: formContent.trim(),
          description: formDescription.trim(),
        });
      }

      if (result.success) {
        closeEditor();
        await fetchSkills();
        if (onSkillsChanged) onSkillsChanged();
      } else {
        setFormError(result.error || 'Failed to save skill');
      }
    } catch (err) {
      const serverViolations = err.data?.violations;
      if (serverViolations?.length) {
        setViolations(serverViolations);
        setFormError(`Server rejected: ${serverViolations.length} blocked pattern(s) found.`);
      } else {
        setFormError(err.message || 'Failed to save skill');
      }
    } finally {
      setFormSaving(false);
    }
  };

  // ── File upload ──

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    setUploadError(null);

    if (!file) return;

    if (!skillService.validateFileExtension(file.name)) {
      setUploadError('Only .txt and .md files are allowed');
      setUploadFile(null);
      return;
    }

    // 50KB limit for uploads
    if (file.size > 50 * 1024) {
      setUploadError('File size must be under 50KB');
      setUploadFile(null);
      return;
    }

    setUploadFile(file);
  };

  const handleUpload = async () => {
    if (!uploadFile) return;

    setFormSaving(true);
    setUploadError(null);

    try {
      const result = await skillService.uploadSkill(uploadFile, {
        name: formName.trim() || undefined,
        description: formDescription.trim() || undefined,
        is_default: formIsDefault,
      });

      if (result.success) {
        setUploadFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        closeEditor();
        await fetchSkills();
        if (onSkillsChanged) onSkillsChanged();
      } else {
        setUploadError(result.error || 'Upload failed');
      }
    } catch (err) {
      setUploadError(err.message || 'Upload failed');
    } finally {
      setFormSaving(false);
    }
  };

  // ── Delete ──

  const handleDelete = async (skillId) => {
    try {
      await skillService.deleteSkill(skillId);
      setDeleteConfirmId(null);
      await fetchSkills();
      if (onSkillsChanged) onSkillsChanged();
    } catch (err) {
      setError(err.message);
    }
  };

  // ── Set default ──

  const handleSetDefault = async (skillId, currentlyDefault) => {
    try {
      if (currentlyDefault) {
        await skillService.clearDefault();
      } else {
        await skillService.setDefault(skillId);
      }
      await fetchSkills();
      if (onSkillsChanged) onSkillsChanged();
    } catch (err) {
      setError(err.message);
    }
  };

  // ── Highlight violations in content ──
  const renderHighlightedContent = () => {
    if (!formContent || violations.length === 0) return null;

    // Sort violations by position
    const sorted = [...violations].sort((a, b) => a.position - b.position);

    const parts = [];
    let lastIdx = 0;

    sorted.forEach((v, i) => {
      if (v.position > lastIdx) {
        parts.push(
          <span key={`text-${i}`}>{formContent.slice(lastIdx, v.position)}</span>
        );
      }
      parts.push(
        <mark
          key={`violation-${i}`}
          className="skills-manager__violation-highlight"
          title={v.reason}
        >
          {v.match}
        </mark>
      );
      lastIdx = v.position + v.match.length;
    });

    if (lastIdx < formContent.length) {
      parts.push(<span key="text-end">{formContent.slice(lastIdx)}</span>);
    }

    return <div className="skills-manager__highlight-overlay">{parts}</div>;
  };

  // ════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════

  return (
    <div className="skills-manager-wrapper">
    <div className="skills-manager">
      {/* Header */}
      <div className="skills-manager__header">
        <div className="skills-manager__header-left">
          <button className="skills-manager__back-btn" onClick={onClose} title="Back">
            ← Back
          </button>
          <h2 className="skills-manager__title">⚡ Skills</h2>
          <span className="skills-manager__count">
            {skills.length} skill{skills.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="skills-manager__header-right">
          <button
            className="skills-manager__guide-btn"
            onClick={() => setShowGuidance(!showGuidance)}
          >
            {showGuidance ? '✕ Close Guide' : '📖 Do\'s & Don\'ts'}
          </button>
          {!editorMode && (
            <button className="skills-manager__create-btn" onClick={openCreateEditor}>
              + New Skill
            </button>
          )}
        </div>
      </div>

      {/* Guidance Panel */}
      {showGuidance && (
        <div className="skills-manager__guidance">
          <div className="skills-manager__guidance-section">
            <h3 className="skills-manager__guidance-title skills-manager__guidance-title--do">
              ✅ What to include in a skill
            </h3>
            <ul className="skills-manager__guidance-list">
              {GUIDANCE.dos.map((item, i) => (
                <li key={i} className="skills-manager__guidance-item">
                  <span className="skills-manager__guidance-icon">{item.icon}</span>
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="skills-manager__guidance-section">
            <h3 className="skills-manager__guidance-title skills-manager__guidance-title--dont">
              🚫 What NOT to include (will be rejected)
            </h3>
            <ul className="skills-manager__guidance-list">
              {GUIDANCE.donts.map((item, i) => (
                <li key={i} className="skills-manager__guidance-item">
                  <span className="skills-manager__guidance-icon">{item.icon}</span>
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>
          </div>
          <p className="skills-manager__guidance-note">
            Skills tell the AI <strong>how</strong> to present answers — not <strong>where</strong> to find data.
            The system already knows your tables, columns, and SQL rules.
          </p>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="skills-manager__error-banner">
          ❌ {error}
          <button onClick={() => setError(null)} className="skills-manager__error-dismiss">✕</button>
        </div>
      )}

      {/* Editor */}
      {editorMode && (
        <div className="skills-manager__editor">
          <h3 className="skills-manager__editor-title">
            {editorMode === 'create' ? 'Create New Skill' : `Edit: ${editingSkill?.name}`}
          </h3>

          {/* Name */}
          <div className="skills-manager__field">
            <label className="skills-manager__label">Name *</label>
            <input
              type="text"
              className="skills-manager__input"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="e.g. Regional Sales Summary"
              maxLength={100}
            />
          </div>

          {/* Description */}
          <div className="skills-manager__field">
            <label className="skills-manager__label">Description (optional)</label>
            <input
              type="text"
              className="skills-manager__input"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              placeholder="Brief description of what this skill does"
            />
          </div>

          {/* Content — typed or uploaded */}
          <div className="skills-manager__field">
            <div className="skills-manager__label-row">
              <label className="skills-manager__label">Skill Instructions *</label>
              <span className="skills-manager__char-count">
                {formContent.length} / 5,000
              </span>
            </div>

            {/* Textarea with violation overlay */}
            <div className="skills-manager__textarea-wrapper">
              <textarea
                className={`skills-manager__textarea ${violations.length > 0 ? 'skills-manager__textarea--error' : ''}`}
                value={formContent}
                onChange={(e) => setFormContent(e.target.value)}
                placeholder={
                  'Example:\n' +
                  '- Always show amounts in Lakhs (divide INR by 100,000)\n' +
                  '- Group results by Region first, then by Brand\n' +
                  '- Include YoY comparison for all metrics\n' +
                  '- Keep responses concise with tables where possible'
                }
                rows={10}
                maxLength={5000}
              />
            </div>

            {/* Violations list */}
            {violations.length > 0 && (
              <div className="skills-manager__violations">
                <div className="skills-manager__violations-header">
                  ⚠️ {violations.length} blocked pattern{violations.length !== 1 ? 's' : ''} found:
                </div>
                {violations.map((v, i) => (
                  <div key={i} className="skills-manager__violation-item">
                    <span className="skills-manager__violation-match">"{v.match}"</span>
                    <span className="skills-manager__violation-reason">— {v.reason}</span>
                  </div>
                ))}
              </div>
            )}

            {/* File upload option (only for create mode) */}
            {editorMode === 'create' && (
              <div className="skills-manager__upload-section">
                <span className="skills-manager__upload-label">Or upload a file:</span>
                <div className="skills-manager__upload-row">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".txt,.md"
                    onChange={handleFileSelect}
                    className="skills-manager__file-input"
                  />
                  {uploadFile && (
                    <button
                      className="skills-manager__upload-btn"
                      onClick={handleUpload}
                      disabled={formSaving}
                    >
                      {formSaving ? 'Uploading...' : `Upload ${uploadFile.name}`}
                    </button>
                  )}
                </div>
                {uploadError && (
                  <div className="skills-manager__upload-error">❌ {uploadError}</div>
                )}
                <p className="skills-manager__upload-hint">
                  Accepted formats: .txt, .md — Max 50KB
                </p>
              </div>
            )}
          </div>

          {/* Default checkbox */}
          {editorMode === 'create' && (
            <div className="skills-manager__field skills-manager__field--inline">
              <label className="skills-manager__checkbox-label">
                <input
                  type="checkbox"
                  checked={formIsDefault}
                  onChange={(e) => setFormIsDefault(e.target.checked)}
                />
                <span>Set as default skill</span>
              </label>
              <span className="skills-manager__hint">
                Default skill is automatically applied to new conversations
              </span>
            </div>
          )}

          {/* Form error */}
          {formError && (
            <div className="skills-manager__form-error">❌ {formError}</div>
          )}

          {/* Actions */}
          <div className="skills-manager__editor-actions">
            <button
              className="skills-manager__cancel-btn"
              onClick={closeEditor}
              disabled={formSaving}
            >
              Cancel
            </button>
            <button
              className="skills-manager__save-btn"
              onClick={handleSave}
              disabled={formSaving || violations.length > 0 || !formName.trim() || !formContent.trim()}
            >
              {formSaving ? 'Saving...' : editorMode === 'create' ? 'Create Skill' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* Skills list */}
      <div className="skills-manager__list">
        {loading ? (
          <div className="skills-manager__loading">Loading skills...</div>
        ) : skills.length === 0 && !editorMode ? (
          <div className="skills-manager__empty">
            <div className="skills-manager__empty-icon">⚡</div>
            <h3>No skills yet</h3>
            <p>
              Skills tell the AI how to format and analyze your data.
              Create your first skill to get started.
            </p>
            <button className="skills-manager__create-btn" onClick={openCreateEditor}>
              + Create Your First Skill
            </button>
          </div>
        ) : (
          skills.map(skill => (
            <div
              key={skill.skill_id}
              className={`skills-manager__card ${skill.is_default ? 'skills-manager__card--default' : ''}`}
            >
              <div className="skills-manager__card-header">
                <div className="skills-manager__card-title-row">
                  <span className="skills-manager__card-icon">
                    {skill.is_default ? '★' : '☆'}
                  </span>
                  <h4 className="skills-manager__card-name">{skill.name}</h4>
                  {skill.is_default && (
                    <span className="skills-manager__card-badge">DEFAULT</span>
                  )}
                  {skill.source === 'uploaded' && (
                    <span className="skills-manager__card-source" title={skill.original_filename}>
                      📄 {skill.original_filename}
                    </span>
                  )}
                </div>
                {skill.description && (
                  <p className="skills-manager__card-desc">{skill.description}</p>
                )}
              </div>

              <div className="skills-manager__card-content">
                <pre className="skills-manager__card-preview">{skill.content}</pre>
              </div>

              <div className="skills-manager__card-footer">
                <span className="skills-manager__card-meta">
                  {skill.content?.length || 0} chars •
                  {skill.updated_at
                    ? ` Updated ${new Date(skill.updated_at).toLocaleDateString()}`
                    : ''
                  }
                </span>
                <div className="skills-manager__card-actions">
                  <button
                    className={`skills-manager__action-btn ${skill.is_default ? 'skills-manager__action-btn--active' : ''}`}
                    onClick={() => handleSetDefault(skill.skill_id, skill.is_default)}
                    title={skill.is_default ? 'Remove default' : 'Set as default'}
                  >
                    {skill.is_default ? '★ Default' : '☆ Set Default'}
                  </button>
                  <button
                    className="skills-manager__action-btn"
                    onClick={() => openEditEditor(skill)}
                  >
                    ✏️ Edit
                  </button>
                  {deleteConfirmId === skill.skill_id ? (
                    <div className="skills-manager__delete-confirm">
                      <span>Delete?</span>
                      <button
                        className="skills-manager__delete-yes"
                        onClick={() => handleDelete(skill.skill_id)}
                      >
                        Yes
                      </button>
                      <button
                        className="skills-manager__delete-no"
                        onClick={() => setDeleteConfirmId(null)}
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <button
                      className="skills-manager__action-btn skills-manager__action-btn--danger"
                      onClick={() => setDeleteConfirmId(skill.skill_id)}
                    >
                      🗑️ Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
    </div>
  );
};

export default SkillsManager;
